import React, { useMemo } from 'react';
import { withSuspense } from 'venn-utils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BrandLogo, ButtonTrigger, getTextThemeProvider, Icon, MenuActions } from 'venn-ui-kit';
import { noop } from 'lodash';
import { Checkbox } from '@/components/ui/checkbox';
import { DataSourceFilterEnum } from '../types';
import CheckboxMenuSelectAll from '../../../../../../../../venn-ui-kit/src/components/drop-menu/menus/CheckboxMenuSelectAll';
import { filterMenuItemStyle } from '../shared/constants';
import { useDataSourceFilterState } from './useDataSourceFilterState';
import { DataSourceFilterActions, useDataSourceFilterActions } from './useDataSourceFilterActions';
import { cn } from '@/lib/utils';

const fieldName = 'Data Source';

interface DataSourceFilterProps {
  onFilter: (updated: DataSourceFilterEnum[]) => void;
  selected: DataSourceFilterEnum[];
}

const DataSourceFilterInner = ({ selected, onFilter }: DataSourceFilterProps) => {
  const dependencies = useDataSourceFilterState({ selected });
  const updatedDependencies = useMemo(
    () => ({
      ...dependencies,
      mutations: {
        ...dependencies.mutations,
        onFilter,
      },
    }),
    [dependencies, onFilter],
  );
  const performAction = useDataSourceFilterActions(updatedDependencies);
  const {
    state: { open, integrationsCheckboxState, isAllItemsSelected, viewableDataSourcesEnums, isChecked },
  } = dependencies;
  const handleCheckboxChange = (dataSource: DataSourceFilterEnum) => (checked: boolean) =>
    performAction(
      checked
        ? DataSourceFilterActions.SelectDataSource({ dataSource })
        : DataSourceFilterActions.DeselectDataSource({ dataSource }),
    );
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(shouldOpen) =>
        performAction(shouldOpen ? DataSourceFilterActions.OpenMenu() : DataSourceFilterActions.CloseMenu())
      }
      modal={false}
    >
      <DropdownMenuTrigger>
        <ButtonTrigger
          label={fieldName}
          isOpen={open}
          onClick={noop}
          aria-expanded={open}
          aria-haspopup
          className="qa-library-filter-trigger"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-0">
        <CheckboxMenuSelectAll
          allItemsSelected={isAllItemsSelected}
          onClick={() =>
            performAction(
              isAllItemsSelected ? DataSourceFilterActions.DeselectAll() : DataSourceFilterActions.SelectAll(),
            )
          }
        />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            onClick={(e) => {
              e.preventDefault();
              performAction(DataSourceFilterActions.ToggleIntegrations());
            }}
            className={cn('[&_svg]:size-2.5', filterMenuItemStyle)}
          >
            <Checkbox
              key="dataSource-integration"
              className="pointer-events-none"
              state={integrationsCheckboxState}
              onCheckedChange={noop}
            />
            <Icon type="exchange" />
            Integrations
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-0">
            {viewableDataSourcesEnums.map((dataSource) => (
              <DropdownMenuCheckboxItem
                key={`dataSource-${dataSource.source}`}
                onSelect={(e) => e.preventDefault()}
                state={isChecked(dataSource)}
                onCheckedChange={handleCheckboxChange(dataSource)}
                className={filterMenuItemStyle}
              >
                {dataSource.source}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuCheckboxItem
          key="dataSource-userUpload"
          onSelect={(e) => e.preventDefault()}
          state={isChecked(DataSourceFilterEnum.UploadedByMe())}
          onCheckedChange={handleCheckboxChange(DataSourceFilterEnum.UploadedByMe())}
          className={filterMenuItemStyle}
        >
          <Icon type="upload" />
          Uploaded by me
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          key="dataSource-teamUpload"
          onSelect={(e) => e.preventDefault()}
          state={isChecked(DataSourceFilterEnum.UploadedByTeam())}
          onCheckedChange={handleCheckboxChange(DataSourceFilterEnum.UploadedByTeam())}
          className={filterMenuItemStyle}
        >
          <Icon type="upload" />
          Uploaded by my team
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          key="dataSource-vennProvided"
          onSelect={(e) => e.preventDefault()}
          state={isChecked(DataSourceFilterEnum.VennProvided())}
          onCheckedChange={handleCheckboxChange(DataSourceFilterEnum.VennProvided())}
          className={filterMenuItemStyle}
        >
          <BrandLogo height={14} scaleToFit isDark />
          {getTextThemeProvider().brandProvidedLabel}
        </DropdownMenuCheckboxItem>
        <MenuActions
          onCancel={() => performAction(DataSourceFilterActions.Cancel())}
          onApply={() => performAction(DataSourceFilterActions.ApplyFilters())}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DataSourceFilter = withSuspense(null, DataSourceFilterInner);
